/**
 * Frontend contract aligned with Phase 1 vForecast design.
 * When `backend/openapi.json` catches up, regenerate or diff against it.
 */

/** Legacy scaffold roles remain accepted until auth is fully migrated. */
export type UserRole =
  'owner' | 'finance_admin' | 'reviewer' | 'system_admin' | 'admin' | 'finance' | 'viewer'

export type StageCategory = 'committed' | 'open_pipeline' | 'lost'

export type HygieneStatus = 'ok' | 'warning' | 'error'

export type MonthSource = 'computed' | 'override' | 'manual' | 'actual'

export interface User {
  id: string
  email: string
  full_name: string
  role: UserRole
  roles?: UserRole[]
  is_active: boolean
  /** Owner-only flag: list/read every opportunity; edit remains own-only. */
  is_super_owner?: boolean
  created_at?: string
  updated_at?: string
  open_years?: number[]
  default_year?: number
  feature_switches?: Record<string, boolean>
}

export interface TokenResponse {
  access_token: string
  token_type: string
  expires_in: number
  user: User
}

export interface LoginRequest {
  email: string
  password: string
}

export interface OwnerSummary {
  id: string
  email: string
  full_name: string
}

export interface Stage {
  id: string
  code?: string
  name: string
  probability: number
  category: StageCategory
  sort_order?: number
  retired: boolean
  flags?: Record<string, unknown> | null
}

export interface StageCreate {
  code: string
  name: string
  probability: number
  category: StageCategory
  sort_order?: number
  flags?: Record<string, unknown> | null
}

export interface StageUpdate {
  name?: string
  probability?: number
  category?: StageCategory
  sort_order?: number
  flags?: Record<string, unknown> | null
  retired?: boolean
}

export interface ForecastViewCreate {
  code: string
  name: string
  apply_weighting: boolean
  categories: string[]
  stage_ids?: string[] | null
  is_default?: boolean
}

export interface ForecastViewUpdate {
  name?: string
  apply_weighting?: boolean
  categories?: string[]
  stage_ids?: string[] | null
  is_default?: boolean
}

export interface ForecastYearCreate {
  year: number
  is_open?: boolean
}

export interface LookupCreate {
  list_name: string
  code: string
  label: string
  sort_order?: number
}

export interface LookupUpdate {
  label?: string
  sort_order?: number
}

export interface AlertRule {
  id: string
  code: string
  payload: Record<string, unknown>
}

export interface ClosedMonth {
  id: string
  year: number
  month: number
}

export interface ForecastView {
  id: string
  code: string
  name: string
  apply_weighting: boolean
  categories?: string[]
  stage_ids?: string[] | null
  is_default?: boolean
  /** @deprecated use apply_weighting */
  weighted?: boolean
}

export interface ForecastYear {
  id?: string
  year: number
  is_open: boolean
  /** @deprecated use is_open */
  open?: boolean
  label?: string
}

export interface BillingType {
  code: string
  name: string
}

export interface RevenuePattern {
  code: string
  name: string
  method: 'headcount' | 'manual_monthly'
}

export interface RevenueRuleDefinition {
  id: string
  version: number
  billing_types: BillingType[]
  patterns: RevenuePattern[]
  allowed_combinations: Array<{ billing_type_code: string; revenue_pattern_code: string }>
  payload?: Record<string, unknown>
}

export interface RevenueRuleVersionSummary {
  id: string
  version: number
  effective_from: string
  summary: string
}

export interface WorkedExampleResult {
  name: string
  ok: boolean
  detail: string
}

export interface RevenueRuleMonthDelta {
  year: number
  month: number
  delta: string
}

export interface RevenueRuleImpact {
  before_total: string
  after_total: string
  month_deltas: RevenueRuleMonthDelta[]
}

export interface RevenueRulePreview {
  worked_examples: WorkedExampleResult[]
  impact: RevenueRuleImpact
  closed_months_touched: Array<{ year: number; month: number }>
}

export interface RevenueRuleActivateResult {
  id: string
  version: number
  effective_from: string
}

export interface Customer {
  id: string
  name: string
  active?: boolean
  created_at?: string
}

export type CustomFieldType = 'text' | 'pick_list' | 'number' | 'date'

export interface CustomFieldDefinition {
  code: string
  label: string
  type: CustomFieldType
  options?: string[]
  sort_order: number
}

export interface SeededHygieneCheck {
  code: string
  label: string
  order: number
  pattern_derived: boolean
  enabled: boolean
  threshold?: number | null
}

export interface GenericHygieneCheck {
  code: string
  label: string
  kind: string
  threshold: number
  enabled: boolean
  sort_order: number
}

export interface HygieneChecksConfig {
  seeded_checks: SeededHygieneCheck[]
  generic_checks: GenericHygieneCheck[]
  settings: {
    seeded?: Record<string, { enabled?: boolean; threshold?: number }>
    generic?: GenericHygieneCheck[]
  }
}

export interface AiChannelToggles {
  chat: boolean
  voice: boolean
  mailbox: boolean
  sow: boolean
  ask: boolean
}

export interface AiSystemSettings {
  ai_enabled: boolean
  provider: 'mock' | 'anthropic'
  api_key_configured: boolean
  channels: AiChannelToggles
  ask_freeform?: boolean
  ask_freeform_tools?: string[]
  ask_freeform_max_rounds?: number
  ask_freeform_timeout_s?: number
  deployments: { intent: string; extract: string; interpret: string }
  stage_a_timeout_s: number
  stage_b_timeout_s: number
  message_max_chars: number
  paste_trivial_max: number
  attachment_max_chars: number
  daily_turn_limit: number
  session_inactivity_minutes: number
  retention_raw_months: number
  redaction_on: boolean
}

/** PATCH body may include write-only api_key (never returned on GET). */
export type AiSystemSettingsPatch = Partial<
  Omit<AiSystemSettings, 'api_key_configured'> & { api_key?: string }
>

export interface AiEvidenceSpan {
  text?: string
  start?: number
  end?: number
  field?: string
  [key: string]: unknown
}

export interface AiProposalCard {
  id: string
  action: string
  opportunity_id?: string | null
  base_version?: number | null
  resolved_fields?: Array<{ field?: string; old?: unknown; new?: unknown; ai_suggested?: boolean }>
  evidence?: AiEvidenceSpan[] | null
  assumptions?: unknown[] | null
  missing_fields?: string[]
  needs_user_check?: string[]
  source_label?: string | null
  friction?: string | null
  op_acks?: string[] | null
  lift_eligible?: boolean
  status: string
  /** Set when status is failed after Confirm (server apply error). */
  failure_message?: string | null
  candidate_opportunity_ids?: string[] | null
  post_confirm_version?: number | null
  sort_order?: number
}

export interface AiTurnSummary {
  id: string
  channel: string
  kind: string
  outcome?: string | null
  created_at: string
}

export interface AiInteractionSummary {
  id: string
  ai_turn_id: string
  model_deployment: string
  latency_ms?: number | null
  failure_code?: string | null
  injection_suspected?: boolean
  created_at: string
}

export interface AiBatchPayload {
  id: string
  soft_limit: number
  hard_cap: number
  cards: AiProposalCard[]
}

export interface ChatTurnResponse {
  turn_id?: string | null
  outcome?: string | null
  batch_id?: string | null
  batch?: AiBatchPayload | null
  message?: string | null
  answer?: string | null
  query_id?: string | null
  supported_questions?: Array<{ query_id: string; label: string }> | null
  tool_trace?: Array<{ tool?: string; ok?: boolean }> | null
  retryable?: boolean | null
  form_fallback?: boolean | null
}

export interface AiAskCatalogueQuestion {
  query_id: string
  label: string
}

export interface AiAskCatalogueResponse {
  questions: AiAskCatalogueQuestion[]
  ask_freeform?: boolean
}

export interface AiCombinedPreview {
  batch_id: string
  accepted_count: number
  cards: AiProposalCard[]
}

export interface AiFinanceSettings {
  soft_limit: number
  hard_cap: number
  hard_block_families: Array<'mark_lost' | 'create' | 'update'>
  still_active_suppression_days: number
  still_active_escalate_after: number
  stall_owner_change_grace_days: number
  stalled_threshold_days: number
  contract_end_lead_days: number
  date_conventions: { start: string; end: string; through: string }
  fiscal_year_start: string | null
}

export interface LookupValue {
  id: string
  list_name?: string
  code: string
  label: string
  sort_order?: number
}

export interface OpportunityMonth {
  year: number
  month: number
  amount: string
  currency: string
  source: MonthSource
  headcount?: number | null
  closed?: boolean
}

export interface Opportunity {
  id: string
  customer_id: string
  customer_name: string
  crm_opportunity_id?: string | null
  name: string
  sow_reference?: string | null
  forecast_driver_id?: string | null
  forecast_driver_code?: string | null
  stage_id: string
  stage_name?: string
  stage_code?: string | null
  stage_category?: StageCategory | string | null
  probability: number
  start_date: string
  end_date: string
  billing_type_code: string
  revenue_pattern_code: string
  base_headcount?: number | null
  bill_rate?: string | null
  owner_id: string
  renews_opportunity_id?: string | null
  owner?: OwnerSummary
  rationale?: string | null
  tcv_override?: string | null
  currency: string
  legacy_billing_type_note?: string | null
  remapped_from_2026: boolean
  is_removed?: boolean
  version: number
  last_updated_at?: string
  months?: OpportunityMonth[]
  /** Present on some read paths for manual_monthly edit round-trip. */
  month_inputs?: OpportunityMonthInput[]
  custom_values?: Record<string, string | number>
  hygiene_status?: HygieneStatus
  hygiene_message?: string | null
  hygiene_code?: string | null
  hygiene_guidance?: string | null
  year_total?: number | string | null
  weighted_total?: number | string | null
  customer_year_total?: number | string | null
}

export interface OpportunityWithOwner extends Opportunity {
  owner: OwnerSummary
}

export interface OpportunityPage {
  items: Opportunity[]
  total: number
  skip: number
  limit: number
  /** When `customer`, skip/limit/total count customers (groups stay intact). */
  page_unit?: 'item' | 'customer'
  /** Packed customer pages when max_lines is used. */
  total_pages?: number | null
  /** 0-based index of the first customer on this packed page. */
  customer_offset?: number | null
}

export interface BulkReassignRequest {
  owner_id: string
  opportunity_ids: string[]
}

export interface BulkEditRequest {
  opportunity_ids: string[]
  stage_id?: string | null
  forecast_driver_code?: string | null
}

export interface BulkEditPreviewItem {
  opportunity_id: string
  opportunity_name: string
  totals: {
    before_tcv: number
    after_tcv: number
    before_weighted: number
    after_weighted: number
    currency: string
  }
}

export interface BulkEditPreviewResponse {
  items: BulkEditPreviewItem[]
}

export interface FinanceOwnerAccount {
  id: string
  email: string
  full_name: string
}

export interface AccessLogEntry {
  id: string
  occurred_at: string
  user_id: string | null
  user_email: string | null
  action: string
  detail: Record<string, unknown> | null
}

export interface AccessLogPage {
  items: AccessLogEntry[]
  total: number
  skip: number
  limit: number
}

export interface OpportunityMonthInput {
  year: number
  month: number
  headcount?: number | null
  amount?: string | null
}

export interface OpportunityMonthOverride {
  year: number
  month: number
  amount: string
}

export interface OpportunityWriteBase {
  customer_id: string
  name: string
  crm_opportunity_id?: string | null
  sow_reference?: string | null
  forecast_driver_code?: string | null
  stage_id: string
  start_date: string
  end_date: string
  billing_type_code: string
  revenue_pattern_code: string
  base_headcount?: number | null
  bill_rate?: string | null
  owner_id?: string | null
  rationale?: string | null
  tcv_override?: string | null
  currency?: string
  month_inputs?: OpportunityMonthInput[]
  month_overrides?: OpportunityMonthOverride[]
  custom_values?: Record<string, string | number | null>
}

export type OpportunityCreate = OpportunityWriteBase

export interface OpportunityUpdate extends Partial<OpportunityWriteBase> {
  /** Optimistic concurrency token expected by the API. */
  expected_version: number
  /** Finance-recorded realized amounts for past/closed months. */
  month_actuals?: OpportunityMonthOverride[]
}

export interface PreviewWarning {
  code: string
  message: string
  severity: 'info' | 'warning' | 'error'
}

export interface PreviewTotals {
  before_tcv: number
  after_tcv: number
  before_weighted: number
  after_weighted: number
  currency: string
}

export interface OpportunityPreviewRequest extends OpportunityWriteBase {
  opportunity_id?: string | null
  version?: number | null
}

export interface OpportunityPreviewResponse {
  before_months: OpportunityMonth[]
  after_months: OpportunityMonth[]
  totals: PreviewTotals
  warnings: PreviewWarning[]
  wipe_overrides_warning?: boolean
  wipe_overrides?: Array<{ year: number; month: number }>
  /** @deprecated use wipe_overrides_warning */
  overrides_will_clear?: boolean
}

export interface ForecastSummary {
  year: number
  view_code: string
  weighted_total: number
  committed_total: number
  upside_total: number
  currency: string
  opportunity_count: number
}

export type ForecastMonthMetric = 'committed' | 'weighted' | 'upside'

export interface ForecastMonthAmount {
  month: number
  committed: number | string
  weighted: number | string
  upside: number | string
}

export interface CustomerMonthlyRollup {
  customer_id: string
  customer_name: string
  months: ForecastMonthAmount[]
  year_committed: number | string
  year_weighted: number | string
  year_upside: number | string
}

export interface ForecastMonthly {
  year: number
  currency: string
  months: ForecastMonthAmount[]
  customers: CustomerMonthlyRollup[]
  opportunity_count: number
}

export interface ConsoleChangeRow {
  id: string
  opportunity_id: string
  opportunity_name: string
  customer_name: string
  owner_name: string
  changed_at: string
  summary: string
}

export interface ConsoleHygieneRow {
  opportunity_id: string
  opportunity_name: string
  customer_name: string
  owner_name: string
  check_code: string
  message: string
  /** Actionable fix text when the API provides it. */
  guidance?: string | null
  severity: HygieneStatus
}

export interface ConsoleAlertRow {
  id: string
  opportunity_id: string
  opportunity_name: string
  owner_name: string
  rule_name: string
  sent_at: string
  response?: string | null
}

export interface ConsoleUnconfirmedRow {
  owner_id: string
  owner_name: string
  owner_email: string
  opportunity_count: number
  week_label: string
}

export interface CustomerRequestRow {
  id: string
  requested_name: string
  notes?: string | null
  requester_name: string
  created_at: string
}

export interface FinanceConsole {
  since_last_publish: string | null
  changes: ConsoleChangeRow[]
  hygiene: ConsoleHygieneRow[]
  alerts: ConsoleAlertRow[]
  unconfirmed: ConsoleUnconfirmedRow[]
  customer_requests: CustomerRequestRow[]
}

export interface ConfigImportSummary {
  stages_created: number
  stages_updated: number
  forecast_years_created: number
  forecast_years_updated: number
  forecast_views_created: number
  forecast_views_updated: number
  lookup_values_created: number
  lookup_values_updated: number
  alert_rules_created: number
  alert_rules_updated: number
  closed_months_created: number
  app_settings_created: number
  app_settings_updated: number
  revenue_rules_appended: number
  feature_switches_skipped: number
}

export interface ExportRequest {
  year: number
  view_code: string
  stage_ids?: string[]
  include_lost?: boolean
}

export interface ExportResponse {
  download_url?: string
  filename?: string
}

export interface PublishRequest {
  name: string
  forecast_year: number
  /** Ignored by the API — baselines always include all non-lost opportunities. */
  view_code?: string | null
  commentary?: string | null
}

export interface PublishResponse {
  id: string
  name: string
  forecast_year: number
  published_at: string
  commentary: string | null
  headline_totals: Record<string, unknown> | null
  rule_version_id: string | null
  created_by_id?: string | null
  is_removed?: boolean
}

export interface ForecastSnapshot {
  id: string
  name: string
  forecast_year: number
  published_at: string
  commentary: string | null
  headline_totals: Record<string, unknown> | null
  rule_version_id: string | null
  created_by_id?: string | null
  is_removed?: boolean
}

export interface RestoreBaselineRequest {
  reason: string
}

export interface RestoreBaselineResponse {
  snapshot_id: string
  name: string
  forecast_year: number
  restored_count: number
  missing_count: number
}

export interface PublishedMonthAmount {
  month: number
  amount: string
  display: string
}

export interface PublishedSnapshotLine {
  opportunity_id: string
  name: string
  year_total_display: string
  customer_name?: string | null
  stage_name?: string | null
  stage_probability?: number | null
  year_total_unweighted?: string | null
  year_total_weighted?: string | null
  bill_rate?: string | null
  base_headcount?: number | null
  months?: PublishedMonthAmount[]
}

export interface PublishedCurrent {
  id: string
  name: string
  forecast_year: number
  published_at: string
  commentary: string | null
  headline_totals: Record<string, unknown> | null
  lines: PublishedSnapshotLine[]
}

export interface WeeklySummary {
  text: string
}

export interface UserPage {
  items: User[]
  total: number
  skip: number
  limit: number
}

export interface EmailDeliveryRow {
  id: string
  recipient: string
  subject: string
  status: string
  provider_message_id: string | null
  last_error: string | null
  created_at: string
}

export interface AlertMine {
  id: string
  opportunity_id: string
  opportunity_name: string
  rule_name: string
  rule_code?: string
  sent_at: string
  alert_rule_id?: string
  ignored_count?: number
}

export interface AlertRespondRequest {
  response: 'sow_signed' | 'revise_start' | 'provide_update'
  sow_reference?: string | null
  new_start_date?: string | null
  update_notes?: string | null
}

export interface ApiErrorBody {
  error: {
    code: string
    message: string
    details: Record<string, unknown>
  }
}

export function isFinanceUser(user: User | null | undefined): boolean {
  if (!user) return false
  const roles = user.roles?.length ? user.roles : [user.role]
  return roles.some(
    (r) => r === 'finance_admin' || r === 'finance' || r === 'admin' || r === 'system_admin',
  )
}

export function isOwnerUser(user: User | null | undefined): boolean {
  if (!user) return false
  const roles = user.roles?.length ? user.roles : [user.role]
  return roles.includes('owner') && !isFinanceUser(user)
}

/** Owners with ``is_super_owner``, plus finance/admin/viewer, see every opportunity. */
export function canViewAllOpportunities(user: User | null | undefined): boolean {
  if (!user) return false
  if (isFinanceUser(user) || isViewerUser(user)) return true
  return isOwnerUser(user) && Boolean(user.is_super_owner)
}

export function isViewerUser(user: User | null | undefined): boolean {
  if (!user) return false
  const roles = user.roles?.length ? user.roles : [user.role]
  return roles.includes('viewer') || roles.includes('reviewer')
}

export function isAdminUser(user: User | null | undefined): boolean {
  if (!user) return false
  const roles = user.roles?.length ? user.roles : [user.role]
  return roles.some((r) => r === 'admin' || r === 'system_admin')
}

/** @deprecated Legacy scaffold stage enum — kept for transitional tests only. */
export type OpportunityStage =
  'prospecting' | 'qualification' | 'proposal' | 'negotiation' | 'won' | 'lost'

/** @deprecated */
export const STAGE_LABELS: Record<OpportunityStage, string> = {
  prospecting: 'Prospecting',
  qualification: 'Qualification',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  won: 'Won',
  lost: 'Lost',
}

/** @deprecated */
export const ALLOWED_TRANSITIONS: Record<OpportunityStage, OpportunityStage[]> = {
  prospecting: ['qualification', 'lost'],
  qualification: ['proposal', 'lost'],
  proposal: ['negotiation', 'lost'],
  negotiation: ['won', 'lost'],
  won: [],
  lost: [],
}
